const translation = {
  // Common
  common: {
    brandName: "AtlasSlicer 2D",
    loading: "Loading...",
    refresh: "Refresh",
    logout: "Logout",
    login: "Login",
    processing: "Processing...",
    error: "Error",
    success: "Success",
    selectLanguage: "Select Language"
  },

  // Languages
  languages: {
    english: "English",
    chinese: "中文"
  },
  
  // Navigation
  nav: {
    home: "Home",
    features: "Features",
    download: "Download",
    pricing: "Pricing",
    buyNow: "Buy License",
    about: "About"
  },
  
  // Footer
  footer: {
    description: "AtlasSlicer 2D is the all-in-one texture atlas workstation built for indie game developers and 2D artists. Combining Rust-powered MaxRects packing, 4-in-1 intelligent slicing, and a real-time animation quality checker. 100% local, offline, and secure.",
    services: "Services",
    contactUs: "Contact Us",
    changelog: "Changelog",
    about: "About",
    userAgreement: "User Agreement",
    privacyPolicy: "Privacy Policy",
    refundPolicy: "Refund Policy",
    followUs: "Follow Us",
    supportTitle: "Customer Support",
    supportDesc: "Have questions about using the app, engine formats, or payment? Contact our support team:",
    customerSupport: "Technical Support",
    copyright: "Copyright © 2025-2026 AtlasSlicer. All rights reserved."
  },
  
  // Home
  home: {
    badge: "Built for Indie Game Devs & 2D Artists",
    title: "AtlasSlicer 2D Workstation",
    subtitle: "Texture Packing · Smart Slicing · Animation Player · Multi-Engine",
    description: "Say goodbye to tedious manual cutting and texture bleeding! AtlasSlicer 2D integrates blazing-fast Rust MaxRects packing, 4-in-1 smart sprite slicing (Alpha contours / regular grids / chroma keying / manual guidelines), and real-time animation playback. Natively compatible with 10 major game engines.",
    getStarted: "Get Lifetime License",
    viewDemo: "Explore Features",
    downloadClient: "Download Client",
    previewTitle: "Texture Atlas & Slicing",
    previewDesc: "Smart bounding boxes, pixel-perfect edge snapping, and seamless multi-engine export.",
    alphaClosed: "✓ Smart Alpha Bounding & Anti-Bleeding",
    features: {
      fast: "Rust Native Packing",
      fastDesc: "Ultra-fast MaxRects packing algorithms with POT/NPOT constraints & Extrude pixel padding to prevent texture bleeding.",
      accurate: "4-in-1 Smart Slicing",
      accurateDesc: "Alpha connected-components detection, regular grids, chroma keying background removal, and freehand guidelines.",
      flexible: "Animation Player",
      flexibleDesc: "Inspect extracted animation frames in real-time with 1-60 dynamic FPS, Loop, and PingPong modes.",
      safe: "10 Engines & Offline",
      safeDesc: "Native export for Unity, Godot 4, Cocos, Phaser, and more. 100% offline with zero cloud telemetry."
    },
    faq: {
      title: "Frequently Asked Questions",
      questions: [
        {
          question: "What makes AtlasSlicer 2D unique?",
          answer: "AtlasSlicer 2D is not just an extraction tool—it is a comprehensive 2D game asset workstation that brings together texture packing (TexturePacker equivalent), multi-mode smart slicing, and real-time animation frame inspection into one seamless desktop application."
        },
        {
          question: "What capabilities does the Texture Packer offer?",
          answer: "Powered by native Rust MaxRects algorithms (supporting BSSF, BLSF, and BAF heuristics), it includes Square Grid Wrap packing, Power-of-Two (POT) and NPOT dimensions, custom padding, and Extrude pixel expansion to completely eliminate texture bleeding in 3D and 2D bilinear rendering."
        },
        {
          question: "Can I slice sprites from an image without any metadata files?",
          answer: "Absolutely! AtlasSlicer 2D features 4 slicing modes: 1. Smart Alpha Contour Analysis (detects irregular sprites by transparency); 2. Regular Grid Division (split by rows/columns or dimensions); 3. Chroma Keying (remove solid backgrounds like green screen); 4. Manual Guidelines (freely place horizontal/vertical cut lines)."
        },
        {
          question: "Which game engines and formats are supported for export?",
          answer: "AtlasSlicer 2D supports 10 major formats out of the box: Unity (.meta/JSON), Godot 4 (.tres), Cocos2d-x (.plist), Phaser 3 (JSON Hash/Array), Starling (XML), LibGDX (.atlas), LayaAir (.atlas/json), Love2D (Lua), CSV spreadsheets, and TexturePacker JSON, plus a fully customizable XML/JSON template engine."
        },
        {
          question: "Will my game artwork ever be uploaded to any server?",
          answer: "Never! AtlasSlicer 2D runs 100% locally on your machine. All packing, slicing, and rendering algorithms execute in local CPU/GPU memory. No assets or project files are ever sent over the network."
        },
        {
          question: "Is AtlasSlicer 2D a subscription or a one-time purchase?",
          answer: "AtlasSlicer 2D is a lifetime purchase (Lifetime License) with zero recurring subscriptions. Upon payment, your license key is delivered instantly to your email, granting permanent access and free updates for life."
        }
      ]
    }
  },
  
  // About
  about: {
    title: "About AtlasSlicer 2D",
    content: "AtlasSlicer 2D is dedicated to delivering the fastest, most reliable 2D asset pipeline tools for indie game developers and digital artists worldwide."
  },
  
  // Features
  features: {
    badge: "AtlasSlicer 2D Capabilities",
    title: "Built for Professional 2D Game Asset Pipelines",
    subtitle: "Seamlessly connecting Texture Packing, Smart Slicing, Animation QC, and Multi-Engine Export.",
    description: "Leave clunky single-purpose utilities and expensive monthly subscriptions behind. AtlasSlicer 2D combines Rust raw computing speed with an intuitive desktop UI to streamline your daily workflow.",
    packer: {
      title: "Rust Native MaxRects Texture Packing",
      description: "Pack hundreds of individual sprites into tight, GPU-optimized texture sheets in milliseconds, cutting down game draw calls drastically.",
      features: [
        "MaxRects packing with BSSF, BLSF, and BAF heuristic algorithms",
        "Square Grid Wrap mode and arbitrary aspect ratios",
        "Power-of-Two (POT 1024/2048/4096) & NPOT dimension controls",
        "Inner Padding & Extrude pixel expansion (prevents texture bleeding & seams)"
      ]
    },
    slicer: {
      title: "4-in-1 Smart Slicing & Sprite Extraction",
      description: "Extract pristine sprite sequences effortlessly from merged sheets, tilesets, or retro game rips.",
      features: [
        "Smart Alpha Contours: Pixel-perfect snapping with adjustable tolerance and min area filter",
        "Regular Grid Slicing: Even division by rows, columns, or fixed pixel dimensions",
        "Chroma Keying: Isolate sprites on solid color backgrounds (green screen) in seconds",
        "Manual Guidelines: Freely place vertical and horizontal slicing lines on canvas"
      ]
    },
    animation: {
      title: "Real-Time Animation QC & Animated Image Export",
      description: "Verify extracted frame sequences instantly and export crisp transparent animated images.",
      features: [
        "Instant animation preview directly from sliced selection",
        "Dynamic 1-60 FPS adjustment with Single-shot, Loop, and PingPong modes",
        "Export transparent GIF / APNG / WebP animations with crisp 1x~8x pixel scaling",
        "Step Forward / Backward frame-by-frame inspection"
      ]
    },
    engines: {
      title: "10 Game Engines & Custom Template Engine",
      description: "Export directly into your game project's native format without manual coordinate conversion.",
      features: [
        "Native export for Unity (.meta/JSON), Godot 4 (.tres), Cocos2d-x (.plist), Phaser 3",
        "Comprehensive support for Starling (XML), LibGDX (.atlas), LayaAir, Love2D (Lua), CSV",
        "TexturePacker JSON (Hash / Array) industry standard compatible",
        "Powerful XML/JSON template engine to export custom schemas for in-house engines"
      ]
    },
    security: {
      title: "100% Offline Privacy & Native Performance",
      description: "Your commercial game assets remain confidential on your computer at all times.",
      features: [
        "Built on native Rust core with lightning-fast execution and tiny memory usage",
        "Zero network dependencies—works flawlessly offline in air-gapped environments",
        "Modern dark and light themes designed for hours of comfortable production work"
      ]
    },
    offlineIllustration: {
      title: "100% Local Sandbox",
      desc: "All packing, slicing, and rendering algorithms execute in local hardware memory",
      encrypted: "🔒 In-Memory Local Compute",
      latency: "⚡ 0ms Zero Network Latency"
    },
    crossIllustration: {
      textureTitle: "4K / 8K",
      textureDesc: "Ultra-High Resolution Atlas Support",
      platformTitle: "Rust + Tauri",
      platformDesc: "Lightweight Native Cross-Platform"
    },
    content: "Make AtlasSlicer 2D the powerhouse of your 2D game asset pipeline!",
    ctaSubtitle: "14-day money-back guarantee · One-time purchase · Lifetime free updates"
  },
  
  // Pricing
  pricing: {
    badge: "Lifetime License · Ad-Free · Free Updates for Life",
    title: "Simple, Transparent Lifetime Pricing",
    subtitle: "Pay once, use forever. No recurring subscriptions, unlock all professional features.",
    planTitle: "AtlasSlicer 2D Pro License",
    planSubtitle: "For indie game developers, studios, and 2D artists",
    lifetime: "Lifetime",
    buyNowBtn: "Buy Securely via Paddle",
    includes: "Includes all professional workstation features:",
    successTitle: "🎉 Congratulations! Purchase Successful!",
    successMessage: "Your lifetime License Key has been sent to your email. Thank you for supporting indie game tool development!",
    errorNotReady: "Payment module is not ready yet. Please try again.",
    errorOpenFailed: "Failed to open checkout window, please refresh and retry.",
    connectingGateway: "Connecting to secure payment gateway...",
    preparingPayment: "Preparing payment component...",
    securityBadge: "Paddle Official Global Secure Checkout",
    paymentMethods: "Supports Visa, MasterCard, PayPal, UnionPay, etc.",
    moneyBackGuarantee: "14-Day Money-Back Guarantee",
    features: {
      packer: "Break the 200-sprite limit: Unlimited sprites, Multi-page Atlas & 4K/8K Ultra-HD export",
      slicer: "4-in-1 Smart Slicing (Alpha / Grid / Chroma Key / Manual Guides) & Batch Rename",
      animation: "Animation QC Player & Transparent GIF / APNG / WebP Export (1x~8x pixel scaling)",
      engines: "Pixel Edge Extrude (Anti-Bleed), 10 Game Engine Formats & Custom XML/JSON Templates",
      offline: "100% Local Offline Execution for Total Art Asset Confidentiality",
      updates: "Lifetime License: 1 Key Activates up to 3 Devices (Self-Service Unbind) & Free Updates"
    }
  },

  // Download
  download: {
    badge: "v1.0.0 Stable Released",
    title: "Download AtlasSlicer 2D Desktop Client",
    description: "Dedicated desktop application built for Windows and macOS. Features drag-and-drop instant packing, 4-in-1 smart slicing, and animation quality inspection. Activate permanently with your License Key.",
    downloadWin: "Download Windows App (.exe)",
    downloadWinInstaller: "Download Windows Installer (.exe)",
    downloadWinPortable: "Download Windows Portable (.zip)",
    downloadWinArm: "Windows ARM64 Edition",
    downloadMac: "macOS Edition (Coming Soon)",
    downloadMacDmg: "Download macOS Client (.dmg)",
    recommendedTag: "Recommended",
    nativeTag: "Native",
    winPlatformDesc: "Supports Windows 10 / 11 64-bit. 100% local offline processing with GPU acceleration.",
    macPlatformDesc: "Supports Apple Silicon (M1/M2/M3/M4) and Intel-based Mac devices.",
    historyTitle: "Release History",
    historyDesc: "Browse and download previous versions as needed.",
    historyInstaller: "Installer (.exe)",
    historyPortable: "Portable (.zip)",
    historyMac: "macOS (.dmg)",
    releaseDate: "Released on",
    currentBadge: "Latest Stable",
    viewChangelog: "Release Notes",
    portableNotice: "Portable zip archive, extract and run directly",
    getLicense: "Get Lifetime License Key",
    backHome: "Back to Home",
    versionInfo: "Version: v1.0.0 Stable | 100% Local & Offline | Windows 10/11 64-bit",
    licenseTitle: "Activation Guide",
    licenseNote: "Free Community Edition works out of the box; enter your License Key in [Upgrade to Pro] or [Settings -> Pro License] to unlock all Pro perks (up to 3 devices per key).",
    smartScreenTip: "If Windows prompts 'Unknown Publisher': Click 'More info' ➔ 'Run anyway'",
    securityTitle: "Security & Installation Guide (Windows SmartScreen Notice)",
    securityDesc: "As an indie tool without commercial code signing certificates, Windows SmartScreen may show an 'Unknown Publisher' or 'Windows protected your PC' prompt. AtlasSlicer 2D runs 100% locally and offline without collecting personal data:",
    securityStep1: "When the SmartScreen prompt pops up, click the 'More info' link",
    securityStep2: "Click 'Run anyway' button to proceed with installation smoothly",
    securityStep3: "Alternatively, you can choose the 'Portable (.zip)' edition to run without installation"
  },

  // Error
  error: {
    refreshPage: "Refresh Page",
    apiConfig: {
      title: "API Configuration Error",
      checkEnv: "Please check your environment variables to ensure API URL and key are configured properly.",
      createFile: "Create .env.local file in the project root and add:",
      reload: "Reload"
    }
  }
};

export default translation;
